"use client";

import styles from "@/styles/cart/index.module.scss";
import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Input, Checkbox, Skeleton, Button } from "react-vant";
import { ArrowDown, DeleteO } from "@react-vant/icons";
import GoodsSelectItem from "@/components/cart/GoodsSelectItem";
import Settlement from "@/components/cart/Settlement";
import EmptyCart from "@/components/cart/EmptyCart";
import Csotd from "@/components/home/Csotd";
import { toast } from "@/components/global/Toast";
import Dialog from "@/components/global/Dialog";
import viewcartApi from "@/service/modules/viewcartApi";
import loginApi from "@/service/modules/loginApi";
import myAccountApi from "@/service/modules/myAccountApi";
import paymentApi from "@/service/modules/paymentApi";
import productApi from "@/service/modules/productApi";
import { getCookie } from "@/utils/cookie";
import { setGtmDataLayer } from "@/utils/gtmDataLayer";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { updateCartList } from "@/store/slices/cartListSlice";
import ExpediteDialog from "@/components/global/ExpediteDialog";

import { cartProductType, discountInfoType } from "@/types/cartProduct";

interface LoadingRows {
  [key: number]: boolean;
}

// 计算已经没有选中的邮费
let currentBatchID: string | null = null;

export default function Cart() {
  const dispatch = useAppDispatch();
  const [effectiveList, setEffectiveList] = useState<cartProductType[]>([]);
  const [invalidityList, setInvalidity] = useState<cartProductType[]>([]);
  const [isFirst, setIsFirst] = useState(true);
  const [showCoupon, setShowCoupon] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteArray, setDeleteArray] = useState<React.Key[]>([]);
  const [loadingRows, setLoadingRows] = useState<LoadingRows>({});
  const userInfo = useAppSelector(
    (state) => state.user.customerInfo
  );
  const [postCode, setPostcode] = useState<number | string>("");
  const [suburb, setSuburb] = useState("");
  const [suburbList, setSuburbList]: any = useState([]);
  const [inputFocus, setInputFocus] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const [credits, setCredits] = useState(0);
  const [points, setPoints] = useState(0);
  const [creditsInput, setCreditsInput]: any = useState("");
  const [pointsInput, setPointsInput]: any = useState("");
  const [coupon, setCoupon]: any = useState();
  const [applyBtnLoading, setApplyBtnLoading] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<any>({});
  const [discountInfo, setDiscountInfo] = useState<discountInfoType>({
    couponDiscountValue: 0,
    credits: 0,
    pointsValue: 0,
  });
  const [isLogin, setIsLogin] = useState(false);
  const [affiliateid, setAffiliateid] = useState<string | undefined>("");
  // 配置一个Uleeid 用于判断 不可用场景
  const [isUleeId, setIsUleeId] = useState(false);
  const [couponPackage, setCouponPackage] = useState<number>(0);
  const [packagesList, setPackagesList] = useState<any>([]);

  const [afterPayJs, setAfterPayJs] = useState("");
  const [afterPayJsLoaded, setAfterPayJsLoaded] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);
  const [selectNum, setSelectNum] = useState(0);

  // 初始化读取cookie中的affiliateid值
  useEffect(() => {
    const initialAffiliateId = getCookie("affiliateid");
    setAffiliateid(initialAffiliateId);
    setIsUleeId(
      affiliateid == "11" ||
        affiliateid == "149" ||
        affiliateid == "1041" ||
        affiliateid == "3" ||
        affiliateid == "119" ||
        affiliateid == "1101" ||
        affiliateid == "1105" ||
        affiliateid == "1099"
    );
    // 定期检查cookie中的affiliateid值的变化
    const checkCookie = setInterval(() => {
      const currentAffiliateId = getCookie("affiliateid");
      if (currentAffiliateId !== affiliateid) {
        setAffiliateid(currentAffiliateId);
      }
    }, 1000); // 每秒检查一次
    // 清理定时器
    return () => clearInterval(checkCookie);
  }, [affiliateid]);

  // 先粗略判断是否登录
  const checkIsLogin = async () => {
    try {
      const res = await loginApi.me();
      setIsLogin(!!res.data.customerID);
      return !!res.data.customerID;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    // 拿到默认postcode
    getDefaultPostcode();
    checkIsLogin();
  }, []);

  const getDefaultPostcode = async () => {
    try {
      const res = await productApi.getDefaultPostcode();
      const { postCode } = res.data;
      setPostcode(postCode);
    } catch {
      // 使用兜底postcode、 redux储存优先、再没有使用默认2000
      setPostcode(Number(userInfo?.postCode || 2000));
    }
  };

  useEffect(() => {
    if (postCode && `${postCode}`.length == 4) {
      getSubUrb();
    } else {
      setSuburb("");
      setSuburbList([]);
    }
  }, [postCode]);

  const getSubUrb = async () => {
    try {
      const res = await productApi.getSuburbByPostcode({ postCode: postCode });
      const { suburbID, suburbs } = res.data;
      if (suburbID && suburbs.length) {
        setSuburb(suburbID);
        setSuburbList(suburbs);
      } else {
        toast.show("Invalid Postcode!");
        setSuburb("");
        setSuburbList([]);
      }
    } catch (error: any) {
      toast.show(error.message);
    }
  };

  useEffect(() => {
    if (suburb && `${postCode}`.length == 4) {
      getList();
    }
  }, [suburb]);

  const getList = async () => {
    try {
      setLoading(true);
      const res = await viewcartApi.getList();
      setLoading(false);
      setIsFirst(false);

      setPageDate(res);

      // 等待列表数据, 推送到Emarsys
      if (res.data && res.data.list && res.data.list.available) {
        // pushScarabQueueEvent(res.data.list.available || [])
      }

      // 将获取的数据同步到 Redux
      dispatch(updateCartList(res));
    } catch (error: any) {
      toast.show(error.message);
      setLoading(false);
      setIsFirst(false);
    }
  };

  const setPageDate = async (res: any) => {
    const {
      data: {
        list: { available, unavailable },
        count,
      },
    } = res;
    const loadRows: LoadingRows = {};
    const rowKeys: Array<number> = [];
    const unSelectList: Array<any> = [];
    const batchID = generateRandomID();
    currentBatchID = batchID;

    setSelectNum(count);
    if (available && available.length) {
      setEffectiveList(available);
      available.forEach((v: any) => {
        // 给所有有效列表的行加上邮费 loading状态
        loadRows[v.productID] = true;

        // 过滤出选中列表
        if (v.appSelectStatus == 1) {
          rowKeys.push(v.productID);
        } else {
          // 过滤出未选中的列表
          unSelectList.push(v);
        }
      });
    } else {
      setEffectiveList([]);
    }
    if (unavailable && unavailable.length) {
      setInvalidity(unavailable);
    } else {
      setInvalidity([]);
    }
    setSelectedRowKeys(rowKeys);
    setLoadingRows(loadRows);

    window.scrollTo({
      top: 0,
      behavior: "smooth", // 添加平滑滚动效果
    });

    // 计算已经选中的邮费，算完后马上计算没有选中的邮费
    if (rowKeys.length) {
      // 登录获取优惠券
      const loginStatus = await checkIsLogin();
      if (loginStatus) {
        await getCouponsList();
      } else {
        await calculateSelectPost();
      }
    } else {
      setPaymentInfo({});
      setDiscountInfo({
        couponDiscountValue: 0,
        credits: 0,
        pointsValue: 0,
      });
      setPackagesList([]);
      setCouponPackage(0);
      setCoupon("");
    }
    if (unSelectList.length) {
      calculateUnSelectPost(unSelectList, batchID);
    }
  };

  // 计算已经选中的邮费
  const calculateSelectPost = async () => {
    try {
      const subName = suburbList.find((v: any) => v.subID == suburb)?.subName;
      const res = await viewcartApi.calculateSelectPost({
        postCode,
        suburb: subName,
        suburbID: suburb,
      });
      setCalculateData(res);
      const {
        data: { cart },
      } = res;
      if (cart.discountInfo && cart.discountInfo.message) {
        setCouponPackage(0);
        setCoupon("");
      }
    } catch (error: any) {
      if (error.message) {
        toast.show(error.message);
      }
    }
  };

  const setCalculateData = (response: any) => {
    const {
      data: {
        cart: { items },
        cart,
      },
    } = response;
    if (!(cart && items)) return;
    setPostTable(items);
    setPaymentInfo(cart);

    if (cart.discountInfo && cart.discountInfo.discountTotal) {
      setDiscountInfo(cart.discountInfo || {});
      setShowCoupon(true);
    } else {
      setDiscountInfo({
        couponDiscountValue: 0,
        credits: 0,
        pointsValue: 0,
      });
    }

    if (cart.discountInfo && cart.discountInfo.points) {
      setPointsInput(cart.discountInfo.points);
    } else {
      setPointsInput("");
    }

    if (cart.discountInfo && cart.discountInfo.credits) {
      setCreditsInput(cart.discountInfo.credits);
    } else {
      setCreditsInput("");
    }

    if (cart.discountInfo && cart.discountInfo.packageID > 0) {
      setCouponPackage(cart.discountInfo.packageID);
    } else {
      setCouponPackage(0);
    }

    // 设置 Tag Assistant Legacy data
    const ecomm_prodid = []
    for (const key in items) {
      if (Object.prototype.hasOwnProperty.call(items, key)) {
        const element = items[key];
        ecomm_prodid.push(element.productID)
      }
    }
    const porductId = ecomm_prodid.join(",")
    const g_params = {
      "remarketing_params": {
        "ecomm_prodid": ecomm_prodid,
        "ecomm_pagetype": "cart",
        "ecomm_totalvalue": cart.subTotal - cart.discount,
        "ecomm_prodname": "",
        "ecomm_catname": "",
        "ecomm_email": ""
      },
      "params": {
        "userId": userInfo?.customerID,
        "categoryId": null,
        "productId": porductId
      }
    }
    setGtmDataLayer(g_params)
  };

  const generateRandomID = () => {
    return Math.random().toString(36).substr(2, 9);
  };

  const calculateUnSelectPost = async (
    unavailableList: Array<any>,
    batchID: any
  ) => {
    if (unavailableList.length === 0) return;

    const selectNum = 10;

    const processBatch = async (batch: Array<any>, batchID: string) => {
      if (batchID !== currentBatchID) {
        // If the batch ID does not match the current batch ID, exit the function
        return;
      }

      const firstTen = batch.map((v: any) => ({
        productID: v.productID,
        quantity: v.quantity,
      }));

      const subName = suburbList.find((v: any) => v.subID === suburb)?.subName;

      const params = {
        postCode: postCode,
        suburb: subName,
        items: firstTen,
        suburbID: suburb,
      };

      try {
        const res = await viewcartApi.calcSingle(params);
        const {
          data: {
            cart: { items },
          },
        } = res;
        setPostTable(items);
      } catch (error: any) {
        toast.show(error.message);
      }
    };

    const batches = [];

    while (unavailableList.length) {
      batches.push(unavailableList.splice(0, selectNum));
    }

    for (const batch of batches) {
      await processBatch(batch, batchID);
    }
  };

  // 写回真实邮费到table中
  const setPostTable = (postItems: any) => {
    const map: any = postItems;
    const newLoadRows: LoadingRows = {};
    for (const key in postItems as Record<number, any>) {
      if (Object.prototype.hasOwnProperty.call(postItems, key)) {
        newLoadRows[key] = false;
      }
    }

    // 写回到 table中
    setEffectiveList((invalidity: any) =>
      invalidity.map((item: any) => ({
        ...item,
        ...map[item.productID],
      }))
    );
    setLoadingRows((loadRows: LoadingRows) => ({
      ...loadRows,
      ...newLoadRows,
    }));
  };

  const applyDiscount = async () => {
    try {
      if (selectedRowKeys.length < 1) {
        toast.show("Please select items！");
        return;
      }
      if (couponPackage < 1 && !coupon && pointsInput < 0 && creditsInput < 0) {
        toast.show(
          "Please use your credits, CrazysPoints, or coupon code to apply the discount."
        );
        return;
      }

      setApplyBtnLoading(true);
      const subName = suburbList.find((v: any) => v.subID == suburb)?.subName;
      const params = {
        coupon: couponPackage > 0 ? "" : coupon,
        credits: creditsInput ? Number(creditsInput) : 0,
        points: pointsInput ? Number(pointsInput) : 0,
        postCode: postCode,
        suburb: subName,
        packageID: couponPackage > 0 ? couponPackage : 0,
      };
      const res = await viewcartApi.applyDiscount(params);
      setApplyBtnLoading(false);
      setCalculateData(res);
      const {
        data: { cart },
      } = res;
      if (cart.discountInfo && cart.discountInfo.message) {
        toast.show(cart.discountInfo.message);
        setCouponPackage(0);
        setCoupon("");
      }
    } catch (error: any) {
      toast.show(error.message);
      setApplyBtnLoading(false);
    }
  };

  // 用于第一次加载页面时，使用优惠券
  const usedCoupon = async ({ id }: any) => {
    const subName = suburbList.find((v: any) => v.subID == suburb)?.subName;
    const params = {
      coupon: "",
      credits: 0,
      points: 0,
      postCode: postCode,
      suburb: subName,
      packageID: id,
    };
    const res = await viewcartApi.applyDiscount(params);
    const {
      data: { cart },
    } = res;
    setApplyBtnLoading(false);
    setCalculateData(res);

    if (cart.discountInfo && cart.discountInfo.message) {
      toast.show(cart.discountInfo.message);
      setCouponPackage(0);
      setCoupon("");
    }
  };

  const compareCart = async () => {
    try {
      if (compareLoading) return false;
      setCompareLoading(true);
      const invalidity = effectiveList
        .filter((v: any) => v.appSelectStatus == 1)
        .map((v: any) => ({
          pid: v.productID,
          qty: v.quantity,
          upgrade: v.upgradeExpress,
          selected: v.appSelectStatus,
        }));
      const diffData = await viewcartApi.compareCart({ items: invalidity });
      setCompareLoading(false);
      const {
        data: { isSame },
      } = diffData;
      if (!isSame) {
        // setLoading(true)
        updataCartList();
        throw new Error(
          "Your shopping cart has been updated due to changes made in another tab. The page will refresh to reflect the latest data."
        );
      }
      return isSame;
    } catch {
      setCompareLoading(false);
    }
  };

  // 快速本地更新购物车
  const updateData = (productID: any, newData: any) => {
    const updatedData: any = effectiveList.map((item: any) => {
      if (item.productID === productID) {
        return { ...item, ...newData };
      }
      return item;
    });
    setEffectiveList(updatedData);
  };

  // 更新购物车列表
  const updataCartList = async () => {
    // postCode没有值时，从登录接口获取
    if (postCode && `${postCode}`.length && suburb) {
      getList();
    } else {
      try {
        const res = await loginApi.me();
        setPostcode(res.data.postCode);
      } catch (error: any) {
        toast.show(error.message);
      }
    }
  };

  // 更新购物车（数量、upgrade、select）
  const updateCart = async (type: string | number, value: any, items: any) => {
    try {
      if (loading) return false;
      const { productID, appSelectStatus, upgradeExpress, quantity } = items;
      // 快速更新购物车数据
      switch (type) {
        case "qty":
          updateData(items.productID, { quantity: value });
          break;
        case "upgrade":
          updateData(items.productID, { upgradeExpress: value ? 1 : 0 });
          break;
        case "selected":
          setSelectedRowKeys((prev: any) =>
            value
              ? [...prev, productID]
              : prev.filter((item: any) => item !== productID)
          );
          break;
      }

      const isSame = await compareCart();
      if (!isSame) return;
      setLoading(true);

      const record = {
        pid: productID,
        qty: quantity,
        upgrade: upgradeExpress,
        selected: appSelectStatus,
      };

      switch (type) {
        case "qty":
          record.qty = parseInt(value);
          break;
        case "upgrade":
          record.upgrade = value ? 1 : 0;
          break;
        case "selected":
          record.selected = value ? 1 : 0;
          break;
      }
      const res: any = await viewcartApi.updateCart(record);

      if (res.data) {
        updataCartList();
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      if (error.message) {
        toast.show(error.message);
      }
    }
  };

  // 批量删除购物车点击
  const betchDelete = (selectedRowKeys: any) => {
    if (selectedRowKeys.length === 0) {
      toast.show("Please select items!");
      return;
    }
    setDeleteArray(selectedRowKeys);
    setIsModalOpen(true);
  };

  // 删除购物车,批量删除
  const deleteCart = async () => {
    try {
      setDeleteLoading(true);
      const isSame = await compareCart();
      if (!isSame) return;

      await viewcartApi.deleteCart({ pids: deleteArray });
      setIsModalOpen(false);
      setDeleteLoading(false);
      toast.show("Successfully!");
      updataCartList();
    } catch (error: any) {
      setIsModalOpen(false);
      setDeleteLoading(false);
      if (error.message) {
        toast.show(error.message);
      }
    }
  };

  // 下拉框选择
  const qtyChange = (record: any, value: string | number) => {
    if (Number(value) > 0) {
      updateCart("qty", value, record);
    } else {
      // 删除
      // deleteCart()
      setDeleteArray([record.productID]);
      setIsModalOpen(true);
    }
  };

  // 单独删除
  const deleteItem = (record: any) => {
    setDeleteArray([record.productID]);
    setIsModalOpen(true);
  };

  // 单独express
  const expressChange = (record: any, value: any) => {
    updateCart("upgrade", value, record);
  };

  const onSelect = (record: any, selected: boolean) => {
    updateCart("selected", selected, record);
  };

  const onSelectAll = async (selected: boolean) => {
    try {
      setLoading(true);
      await viewcartApi.selectAll({ selected: selected ? 1 : 0 });
      updataCartList();
    } catch (error: any) {
      if (error.message) {
        toast.show(error.message);
      }
      setLoading(false);
    }
  };

  const slectCoupons = async (couponPackageId: number) => {
    setCouponPackage(couponPackageId);
  };

  const couponInputChange = (value: string) => {
    setCoupon(value);
    setCouponPackage(0);
  };

  const getCreditsList = async () => {
    try {
      const res = await myAccountApi.creditsList();
      if (res.data) {
        const {
          credits: { credits, points },
        } = res.data;
        setCredits(credits);
        setPoints(points);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getCouponsList = async () => {
    try {
      const res = await myAccountApi.getCouponsList();
      if (res.data) {
        const { packages } = res.data;
        setPackagesList(packages);
        if (packages.length > 0) {
          // 使用券包获取金额
          usedCoupon(packages[0]);
          setCouponPackage(packages[0].id);
        } else {
          // 计算选中的商品
          calculateSelectPost();
          setCouponPackage(0);
        }
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (couponPackage > 0 && packagesList.length > 0) {
      const hasCoupons = packagesList.find(
        (item: any) => item.id === couponPackage
      );
      if (hasCoupons) {
        setCoupon(hasCoupons.name);
      }
    }
  }, [couponPackage, packagesList]);

  useEffect(() => {
    if (isLogin) {
      getCreditsList();
    }
  }, [isLogin]);

  // // 注入Emarsys代码
  // const RECOMMENDER_SUBDOMAIN = 'https://recommender.scarabresearch.com/js/1CBC47CFE1C81625/scarab-v2.js';
  // const CDN_SUBDOMAIN = 'http://cdn.scarabresearch.com/js/1CBC47CFE1C81625/scarab-v2.js';

  // // 推送到scarabQueue事件
  // function pushScarabQueueEvent(cartList: any) {
  //   if (window && (window as any).ScarabQueue) {
  //     if (localStorage.getItem('userEmail')) {
  //       (window as any).ScarabQueue.push(['setEmail', localStorage.getItem('userEmail')]);
  //     }

  //     const scarabQueueCartData = cartList.map(function (item: any) {
  //       return {
  //         item: item.productID,
  //         price: Number(item.price),
  //         quantity: Number(item.quantity)
  //       };
  //     });

  //     (window as any).ScarabQueue.push(['cart', scarabQueueCartData]);
  //     (window as any).ScarabQueue.push(['go']);
  //   } else {
  //     console.log('no window.ScarabQueue');
  //   }
  // }

  // useEffect(() => {
  //   const subdomain = document.location.protocol === 'https:' ? RECOMMENDER_SUBDOMAIN : CDN_SUBDOMAIN;
  //   const id = 'scarab-js-api';

  //   if (document.getElementById(id)) return;

  //   const js = document.createElement('script');
  //   js.id = id;
  //   js.src = subdomain;

  //   const fs = document.getElementsByTagName('script')[0];
  //   fs?.parentNode?.insertBefore(js, fs);

  //   js.onload = () => {
  //     if (effectiveList && effectiveList.length) pushScarabQueueEvent(effectiveList)
  //   }

  //   return () => {
  //     js.remove();
  //   };
  // }, []);

  // 接口获取afterpay-express-js的url
  useEffect(() => {
    const fetchAfterpayScriptUrl = async () => {
      try {
        const res = await paymentApi.getAfterPayExpressJs();
        if (res.data) setAfterPayJs(res.data.jsUrl);
      } catch (error) {
        console.error("Failed to fetch afterpay-express-js URL:", error);
      }
    };

    fetchAfterpayScriptUrl();
  }, []);

  // 注入afterpay-express-js
  useEffect(() => {
    if (afterPayJs) {
      const script = document.createElement("script");
      script.src = afterPayJs;
      script.async = true;
      script.onload = () => {
        setAfterPayJsLoaded(true);
        console.log(`Script loaded successfully: ${afterPayJs}`);
      };
      script.onerror = () => {
        console.error(`Failed to load script: ${afterPayJs}`);
      };

      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [afterPayJs]);

  // 价格变化，重新初始化afterpay-express
  useEffect(() => {
    if (
      afterPayJsLoaded &&
      paymentInfo &&
      paymentInfo.total > 0 &&
      paymentInfo.total < 3000
    ) {
      handleInitAfterpay();
    }
  }, [afterPayJsLoaded, paymentInfo]);

  // 初始化afterpay-express
  const handleInitAfterpay = async () => {
    if (typeof (window as any).AfterPay == "undefined") {
      console.log("can not use afterpay");
      return;
    }

    (window as any).AfterPay.initializeForPopup({
      countryCode: "AU",
      target: "#afterpayBtn",
      shippingOptionRequired: true,
      buyNow: true,
      onCommenceCheckout: async function (actions: any) {
        try {
          const res = await paymentApi.getAfterPayExpressToken();
          if (res.data && res.data.express) {
            actions.resolve(res.data.express.token);
          }
        } catch (error) {
          console.error("Failed to fetch script URL:", error);
        }
      },
      onComplete: async function (completeData: any) {
        if (completeData.data.status == "SUCCESS") {
          try {
            // 设置loading状态
            setPaymentLoading(true);

            const res = await paymentApi.afterpayExpressCapture({
              token: completeData.data.orderToken,
            });

            if (res.data && res.data.link) {
              // 排除code 194012，194015，194018 因为金额变化的错误，不要提示
              if (
                res.data.code != 194012 &&
                res.data.code != 194015 &&
                res.data.code != 194018 &&
                res.data.message
              )
                toast.show(res.data.message);

              setTimeout(() => {
                window.location.href = res.data.link;
              }, 200);
            } else if (res.data && res.data.orderID) {
              window.location.href = `/payment-result/after-pay-express/${res.data.orderID}`;
            } else {
              setPaymentLoading(false);
              if (res.data.message) toast.show(res.data.message);
            }
          } catch {
            setPaymentLoading(false);
            toast.show("Afterpay Express Capture failed, Please try again");
          }
        } else if (
          completeData.data.status != "SUCCESS" &&
          completeData.data.status != "CANCELLED"
        ) {
          setPaymentLoading(false);
          toast.show("Afterpay Express Capture failed, Please try again");
        }
      },
      onShippingAddressChange: function (data: any, actions: any) {
        const shippingAddressInfo = {
          id: "1",
          name: "Standard Delivery",
          description: "Provided by Crazysales",
          shippingAmount: {
            amount: paymentInfo.shipping.toFixed(2).toString(),
            currency: "AUD",
          },
          taxAmount: { amount: paymentInfo.gst.toString(), currency: "AUD" },
          orderAmount: {
            amount: paymentInfo.total.toString(),
            currency: "AUD",
          },
        };

        actions.resolve([shippingAddressInfo]);
      },
    });
  };

  // 唤起paypal express
  const handleOpenPaypal = async () => {
    try {
      if (!selectedRowKeys.length || paymentInfo.total <= 0) {
        toast.show("Please select items!");
        return;
      }

      const res = await paymentApi.getPaypalExpressUrl();
      if (res.data && res.data.link) {
        window.location.href = res.data.link;
      } else {
        if (res.data.message) toast.show(res.data.message);
      }
    } catch {
      console.error("Failed to fetch Paypal URL");
    }
  };

  // checkout点击
  const checkoutEvent = (callBack?: () => void) => {
    if (!selectedRowKeys.length) {
      toast.show("Please select items!");
      return;
    }
    callBack?.();
  };

  return (
    <div className={styles["page-view-cart"]}>
      {!isFirst && isLogin ? <ExpediteDialog /> : null}
      {
        // 先展示骨架屏
        isFirst ? (
          <div className={styles["skeleton-box"]}>
            <Skeleton />
            <Skeleton style={{ marginTop: "28px" }} />
            <Skeleton style={{ marginTop: "28px" }} />
          </div>
        ) : effectiveList.length > 0 || invalidityList.length > 0 ? (
          <div className={styles["cart-content"]}>
            <div className={styles["page-title"]}>Shopping Cart</div>

            <div className={styles["suburb-select"]}>
              <div className={styles["suburb-select-box"]}>
                PostCode
                <Input
                  className={styles["post-code-input"]}
                  value={String(postCode)}
                  onChange={(text) => {
                    const value = String(text)
                      .replace(/[^0-9]/g, "")
                      .slice(0, 4);
                    setPostcode(value);
                  }}
                  type="number"
                  maxLength={4}
                ></Input>
                <select
                  className={styles["suburb-box"]}
                  value={suburb}
                  onChange={(e) => {
                    const selectedSuburbID = e.target.value;
                    setSuburb(selectedSuburbID);
                  }}
                >
                  {suburbList.map((item: any) => (
                    <option key={item.subID} value={item.subID}>
                      {item.subName}
                    </option>
                  ))}
                </select>
                <span
                  className={styles["help"]}
                  onClick={() => setShowHelp(!showHelp)}
                >
                  Help?
                </span>
              </div>

              {!!showHelp && (
                <div className={styles["suburb-help"]}>
                  {`Based on your postcode and suburb, our system will choose the most cost-effective courier. Shipping costs are subject to change depending on your address at check-out.`}
                </div>
              )}
            </div>

            <div className={styles["cart-goods-box"]}>
              <div className={styles["batch-operations-box"]}>
                <Checkbox
                  checked={
                    effectiveList.length > 0 &&
                    effectiveList.reduce(
                      (sum, item) => sum + (item.quantity || 0),
                      0
                    ) === selectNum
                  }
                  onChange={(e) => onSelectAll(e)}
                >
                  Select all
                </Checkbox>

                <DeleteO
                  fontSize={24}
                  color={"#999999"}
                  onClick={() => betchDelete(selectedRowKeys)}
                />
              </div>

              <div className={styles["goods-list"]}>
                {effectiveList.map((item: cartProductType) => (
                  <GoodsSelectItem
                    product={item}
                    loading={loadingRows[item.productID]}
                    key={`${item.productID}${item.quantity}`}
                    onSelect={onSelect}
                    onDelete={deleteItem}
                    onQtyChange={(record, quantity) =>
                      qtyChange(record, quantity)
                    }
                    onExpressChange={(record, upgradeExpress) =>
                      expressChange(record, upgradeExpress)
                    }
                  ></GoodsSelectItem>
                ))}
              </div>

              {invalidityList && invalidityList.length > 0 && (
                <>
                  <div className={styles["invalidity-title"]}>
                    <Image
                      className={styles["invalidity-icon"]}
                      alt="icon"
                      src={`${process.env.IMAGE_DOMAIN}/svg/view-cart/icon-warning.svg`}
                      width={127}
                      height={127}
                    />
                    Sorry, this item has sold out
                  </div>

                  <div className={styles["invalidity-goods-list"]}>
                    {invalidityList.map(
                      (item: cartProductType, index: number) => (
                        <GoodsSelectItem
                          product={item}
                          key={index}
                          onSelect={onSelect}
                          onDelete={deleteItem}
                          onQtyChange={(record, quantity) =>
                            qtyChange(record, quantity)
                          }
                          onExpressChange={(record, upgradeExpress) =>
                            expressChange(record, upgradeExpress)
                          }
                        ></GoodsSelectItem>
                      )
                    )}
                  </div>
                </>
              )}
            </div>

            {!(isUleeId && !isLogin) && (
              <div className={styles["discount-box"]}>
                <div
                  className={`${styles["discount-title"]} ${
                    showCoupon ? styles["discount-title-border"] : ""
                  }`}
                  onClick={() => setShowCoupon(!showCoupon)}
                >
                  <div className={styles["discount-title-text"]}>
                    Optional Discounts and Coupons
                  </div>
                  <ArrowDown className={styles["discount-arrow"]} />
                </div>

                {showCoupon ? (
                  <div className={styles["discount-content"]}>
                    <div
                      className={styles["coupon-tips"]}
                    >{`Some coupons do not apply to "Crazy Sale of The Day" and "Flash Deal" products.`}</div>
                    <div className={styles["coupon-input-box"]}>
                      <Input
                        className={styles["coupon-input"]}
                        value={coupon}
                        onChange={(val) => {
                          couponInputChange(String(val));
                        }}
                        onFocus={() => {
                          setInputFocus(true);
                        }}
                        onBlur={() => {
                          setTimeout(() => {
                            setInputFocus(false);
                          }, 150);
                        }}
                        placeholder={"Coupon/Voucher Code"}
                      />
                    </div>
                    {inputFocus && packagesList && packagesList.length > 0 ? (
                      <div className={styles["coupon-list"]}>
                        {packagesList.map((item: any, index: number) => (
                          <div
                            key={index}
                            className={`${styles["coupon-item"]} ${
                              couponPackage == item.id
                                ? styles["coupon-item-active"]
                                : ""
                            }`}
                            onClick={() => {
                              slectCoupons(item.id);
                            }}
                          >
                            {item.name}
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {isLogin ? (
                      <div className={styles["crazy-points-box"]}>
                        <div className={styles["crazy-points-num"]}>
                          Total Credits:&nbsp;
                          <span style={{ color: "#D70B17" }}>{credits}</span>
                        </div>
                        <div className={styles["crazy-points-desc"]}>
                          (1 Credit = $1) for this purchase
                        </div>

                        <div className={styles["crazy-points-input-box"]}>
                          <Input
                            className={styles["crazy-points-input"]}
                            value={creditsInput}
                            onChange={(val) => {
                              // 只允许输入大于等于0的整数
                              const numericValue = val.replace(/[^0-9]/g, '');
                              setCreditsInput(numericValue);
                            }}
                            placeholder={
                              !isLogin
                                ? "please sign in to check your credits"
                                : ""
                            }
                            clearable
                          />
                        </div>

                        <div className={styles["crazy-points-num"]}>
                          Total CrazyPoints:&nbsp;
                          <span style={{ color: "#D70B17" }}>{points}</span>
                        </div>
                        <div className={styles["crazy-points-desc"]}>
                          1000 points = $10, minimum 1 point is required for
                          each order.
                        </div>

                        <div className={styles["crazy-points-input-box"]}>
                          <Input
                            className={styles["crazy-points-input"]}
                            value={pointsInput}
                            onChange={(val) => {
                              // 只允许输入大于等于0的整数
                              const numericValue = val.replace(/[^0-9]/g, '');
                              setPointsInput(numericValue);
                            }}
                            placeholder={
                              !isLogin
                                ? "please sign in to check your points"
                                : ""
                            }
                            clearable
                          />
                        </div>
                      </div>
                    ) : (
                      <div className={styles["crazy-points-login"]}>
                        Click&nbsp;
                        <Link
                          href={"/login?redirect=%2Fview-cart"}
                          style={{
                            color: "#D70B17",
                            textDecoration: "underline",
                          }}
                        >
                          here
                        </Link>
                        &nbsp; to use CrazyPoints or Credits.
                      </div>
                    )}

                    <Button
                      round
                      type="primary"
                      style={{ fontWeight: "700" }}
                      block
                      onClick={applyDiscount}
                      loading={applyBtnLoading}
                      loadingText="Apply Now"
                    >
                      Apply Now
                    </Button>
                  </div>
                ) : null}
              </div>
            )}

            <Settlement
              selectNum={selectNum}
              paymentInfo={paymentInfo}
              discountInfo={discountInfo}
              isLogin={isLogin}
              isUleeId={isUleeId}
              setPageDate={setPageDate}
              paymentLoading={deleteLoading || paymentLoading}
              checkoutEvent={checkoutEvent}
              handleOpenPaypal={handleOpenPaypal}
            />

            <div className={styles["fixed-total-amount"]}>
              <div className={styles["fixed-content"]}>
                <Checkbox
                  style={{ marginRight: "auto" }}
                  checked={
                    effectiveList.length > 0 &&
                    effectiveList.reduce(
                      (sum, item) => sum + (item.quantity || 0),
                      0
                    ) === selectNum
                  }
                  onChange={(e) => onSelectAll(e)}
                >
                  Select all
                </Checkbox>

                <div className={styles["total-amount-box"]}>
                  <div className={styles["total-amount-title"]}>
                    Total Amount:
                  </div>
                  {typeof paymentInfo.total === "number" &&
                  !isNaN(paymentInfo.total) &&
                  paymentInfo.total > 0 ? (
                    <div className={styles["total-amount-number"]}>
                      ${paymentInfo.total.toFixed(2) || "0.00"}
                    </div>
                  ) : (
                    <div className={styles["total-amount-number"]}>
                      ${"0.00"}
                    </div>
                  )}
                </div>

                <Button
                  round
                  type="primary"
                  loading={deleteLoading}
                  loadingText={`Checkout(${selectNum})`}
                  disabled={Boolean(!selectNum)}
                  style={{
                    marginLeft: "auto",
                    fontSize: "14px",
                    fontWeight: "700",
                  }}
                  onClick={() => {
                    checkoutEvent(() => {
                      window.location.href = `/checkout${
                        isLogin ? "" : "/express"
                      }`;
                    });
                  }}
                >
                  {`Checkout(${selectNum})`}
                </Button>
              </div>
            </div>

            <Dialog
              visible={isModalOpen}
              closeable
              title="Delete Item"
              showConfirmButton={true}
              showCancelButton={true}
              confirmButtonText="Yes"
              cancelButtonText="No"
              onCancel={() => setIsModalOpen(false)}
              onConfirm={deleteCart}
              confirmLoading={deleteLoading}
            >
              <p>Are you sure you want to delete the selected items？</p>
            </Dialog>
          </div>
        ) : (
          <>
            <EmptyCart />
            <div className={styles["csotd-box"]}>
              <Csotd setPageDate={setPageDate} />
            </div>
          </>
        )
      }
    </div>
  );
}
